import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { AudioPracticePlayer } from "@/components/audio-practice-player";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UpgradeModal } from "@/components/upgrade-modal";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";
import {
  Music,
  Upload,
  Loader2,
  Save,
  Library,
  Lock,
  ChevronLeft,
  Trash2,
  Sparkles,
} from "lucide-react";
import { isAudioFreeTrial } from "@/lib/constants";

const ACCEPTED_AUDIO_TYPES = ["audio/mpeg", "audio/wav", "audio/flac", "audio/x-m4a", "audio/mp4", "audio/aac", "audio/ogg"];
const ACCEPTED_EXTENSIONS = ".mp3,.wav,.flac,.m4a,.aac,.ogg";
const MAX_FILE_SIZE_MB = 200;

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPage() {
  useEffect(() => { document.title = "Audio Practice Player | DanceMirror"; }, []);
  const auth = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);
  const [loadedFile, setLoadedFile] = useState<File | null>(null);
  const [loadedSavedId, setLoadedSavedId] = useState<number | null>(null);
  const [loadedTitle, setLoadedTitle] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [playerSettings, setPlayerSettings] = useState<{
    tempo: number;
    pitch: number;
    loopStart: number | null;
    loopEnd: number | null;
  }>({ tempo: 1, pitch: 0, loopStart: null, loopEnd: null });

  const isPaid = ["monthly", "yearly", "lifetime"].includes(auth.user?.subscriptionTier ?? "");

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/videos/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
      if (loadedSavedId === id) {
        setAudioObjectUrl(null);
        setLoadedSavedId(null);
        setLoadedFile(null);
        setLoadedTitle("");
        setSavedId(null);
      }
      toast({ title: "Track deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete track", variant: "destructive" });
    },
  });

  useEffect(() => {
    if (!auth.isLoading && (!auth.isAuthenticated || (!isAudioFreeTrial && !isPaid))) {
      setUpgradeModalOpen(true);
    }
  }, [auth.isLoading, auth.isAuthenticated, isPaid]);

  const { data: audioList } = useQuery<any[]>({
    queryKey: ["/api/videos"],
    enabled: auth.isAuthenticated,
    select: (data) => data?.filter((v) => v.videoType === "audio") ?? [],
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const savedIdParam = params.get("saved");
    if (savedIdParam && auth.isAuthenticated) {
      const id = parseInt(savedIdParam, 10);
      if (!isNaN(id)) {
        loadSavedAudio(id);
      }
    }
  }, [auth.isAuthenticated]);

  const loadSavedAudio = async (id: number) => {
    try {
      const res = await apiRequest("GET", `/api/audio/${id}/url`);
      const data = await res.json();
      setAudioObjectUrl(data.url);
      setLoadedTitle(data.title || "Untitled Audio");
      setLoadedSavedId(id);
      setSavedId(id);
      setLoadedFile(null);
      if (data.settings) {
        setPlayerSettings({
          tempo: data.settings.tempo ?? 1,
          pitch: data.settings.pitch ?? 0,
          loopStart: data.settings.audioLoopStart ?? null,
          loopEnd: data.settings.audioLoopEnd ?? null,
        });
      }
    } catch (err) {
      toast({ title: "Failed to load audio", variant: "destructive" });
    }
  };

  const handleFileSelect = useCallback(async (file: File) => {
    if (!auth.isAuthenticated) {
      setUpgradeModalOpen(true);
      return;
    }
    if (!isAudioFreeTrial && !isPaid) {
      setUpgradeModalOpen(true);
      return;
    }

    if (!ACCEPTED_AUDIO_TYPES.includes(file.type) && !file.name.match(/\.(mp3|wav|flac|m4a|aac|ogg)$/i)) {
      toast({ title: "Unsupported file type", description: "Please upload MP3, WAV, FLAC, M4A, AAC, or OGG files.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast({ title: "File too large", description: `Max file size is ${MAX_FILE_SIZE_MB}MB.`, variant: "destructive" });
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setAudioObjectUrl(localUrl);
    setLoadedFile(file);
    setLoadedSavedId(null);
    setSavedId(null);
    setLoadedTitle(file.name.replace(/\.[^.]+$/, ""));
    setPlayerSettings({ tempo: 1, pitch: 0, loopStart: null, loopEnd: null });
  }, [auth.isAuthenticated, isPaid, toast]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleSaveToLibrary = async () => {
    if (!loadedFile && !loadedSavedId) return;
    if (!isAudioFreeTrial && !isPaid) {
      setUpgradeModalOpen(true);
      return;
    }

    setIsSaving(true);
    try {
      if (loadedSavedId) {
        await apiRequest("PATCH", `/api/audio/${loadedSavedId}/settings`, {
          title: loadedTitle,
          settings: {
            tempo: playerSettings.tempo,
            pitch: playerSettings.pitch,
            audioLoopStart: playerSettings.loopStart,
            audioLoopEnd: playerSettings.loopEnd,
          },
        });
        setSavedId(loadedSavedId);
        queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
        toast({ title: "Settings saved" });
      } else if (loadedFile) {
        setIsUploading(true);
        const urlRes = await apiRequest("POST", "/api/audio/upload-url");
        const { uploadURL, objectPath } = await urlRes.json();

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", uploadURL);
          xhr.setRequestHeader("Content-Type", loadedFile.type || "audio/mpeg");
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
          xhr.onerror = () => reject(new Error("Network error during upload"));
          xhr.send(loadedFile);
        });

        setIsUploading(false);
        setUploadProgress(0);

        const duration = await getAudioDuration(audioObjectUrl!);

        const saveRes = await apiRequest("POST", "/api/audio/confirm", {
          filePath: objectPath,
          title: loadedTitle || loadedFile.name.replace(/\.[^.]+$/, ""),
          audioMimeType: loadedFile.type,
          durationSeconds: duration ? Math.round(duration) : null,
          settings: {
            tempo: playerSettings.tempo,
            pitch: playerSettings.pitch,
            audioLoopStart: playerSettings.loopStart,
            audioLoopEnd: playerSettings.loopEnd,
          },
        });
        const saved = await saveRes.json();
        setSavedId(saved.id);
        setLoadedSavedId(saved.id);
        queryClient.invalidateQueries({ queryKey: ["/api/videos"] });
        toast({ title: "Audio saved to library" });
      }
    } catch (err: any) {
      setIsUploading(false);
      toast({ title: "Failed to save", description: err?.message || "Please try again", variant: "destructive" });
    } finally {
      setIsSaving(false);
      setIsUploading(false);
    }
  };

  const getAudioDuration = (url: string): Promise<number | null> => {
    return new Promise((resolve) => {
      const audio = new Audio(url);
      audio.addEventListener("loadedmetadata", () => resolve(audio.duration));
      audio.addEventListener("error", () => resolve(null));
      setTimeout(() => resolve(null), 5000);
    });
  };

  if (!auth.isAuthenticated || (!isAudioFreeTrial && !isPaid)) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {auth.isAuthenticated ? "Audio Practice is a paid feature" : "Sign in to use Audio Practice"}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Upload music tracks, slow them down without changing the pitch, and loop any section for focused practice.
          </p>
          <Button
            size="lg"
            data-testid="button-upgrade-audio"
            onClick={() => setUpgradeModalOpen(true)}
          >
            {auth.isAuthenticated ? "Upgrade to unlock" : "Sign in to continue"}
          </Button>
        </main>
        <AppFooter />
        <UpgradeModal
          open={upgradeModalOpen}
          onOpenChange={setUpgradeModalOpen}
          mode={auth.isAuthenticated ? "upgrade" : "login"}
          featureName="Audio Practice Player"
          returnTo="/audio"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />

      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
            <Music className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold" data-testid="text-audio-page-title">Audio Practice</h1>
            <p className="text-sm text-muted-foreground">Slow down music without changing pitch · Loop sections</p>
          </div>
        </div>

        {auth.isAuthenticated && !isPaid && isAudioFreeTrial && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-3 text-sm flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <span>
              Audio Practice is <strong>free until July 31, 2026</strong>. After that, it requires a{" "}
              <Link href="/pricing" className="underline underline-offset-2 hover:text-primary">paid plan</Link>.
            </span>
          </div>
        )}

        {audioObjectUrl ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (audioObjectUrl && loadedFile) {
                    URL.revokeObjectURL(audioObjectUrl);
                  }
                  setAudioObjectUrl(null);
                  setLoadedFile(null);
                  setLoadedSavedId(null);
                  setSavedId(null);
                  setLoadedTitle("");
                  window.history.replaceState({}, "", "/audio");
                }}
                data-testid="button-audio-back"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Input
                value={loadedTitle}
                onChange={(e) => setLoadedTitle(e.target.value)}
                className="flex-1 h-8 text-sm"
                placeholder="Track title"
                maxLength={200}
                data-testid="input-audio-title"
              />
              <Button
                size="sm"
                variant={savedId ? "outline" : "default"}
                onClick={handleSaveToLibrary}
                disabled={isSaving || isUploading}
                data-testid="button-save-audio"
              >
                {isUploading ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{uploadProgress}%</>
                ) : isSaving ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</>
                ) : savedId ? (
                  <><Save className="w-4 h-4 mr-1.5" />Save settings</>
                ) : (
                  <><Save className="w-4 h-4 mr-1.5" />Save to Library</>
                )}
              </Button>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
              <AudioPracticePlayer
                key={audioObjectUrl}
                audioUrl={audioObjectUrl}
                fileName={loadedFile?.name}
                fileType={loadedFile?.type}
                initialTempo={playerSettings.tempo}
                initialPitch={playerSettings.pitch}
                initialLoopStart={playerSettings.loopStart}
                initialLoopEnd={playerSettings.loopEnd}
                onSettingsChange={setPlayerSettings}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
                isDragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="audio-upload-dropzone"
            >
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Upload className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-1">Upload an audio track</h3>
              <p className="text-muted-foreground text-sm mb-2">MP3, WAV, FLAC, M4A, AAC, OGG · Max {MAX_FILE_SIZE_MB}MB</p>
              <Button variant="outline" size="sm" data-testid="button-browse-audio" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                Browse files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
                data-testid="input-audio-file"
              />
            </div>

            {audioList && audioList.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Library className="w-4 h-4" /> Saved Tracks
                </h3>
                <div className="space-y-2">
                  {audioList.map((track: any) => (
                    <div
                      key={track.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => loadSavedAudio(track.id)}
                      data-testid={`card-audio-track-${track.id}`}
                    >
                      <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        <Music className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{track.title || "Untitled Audio"}</p>
                        <p className="text-xs text-muted-foreground">
                          {track.durationSeconds ? formatDuration(track.durationSeconds) : ""}
                          {track.settings?.tempo && track.settings.tempo !== 1
                            ? ` · ${Math.round(track.settings.tempo * 100)}% tempo`
                            : ""}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        data-testid={`button-open-audio-${track.id}`}
                      >
                        Open
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(track.id); }}
                        disabled={deleteMutation.isPending}
                        data-testid={`button-delete-audio-${track.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <AppFooter />

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        mode={auth.isAuthenticated ? "upgrade" : "login"}
        featureName="Audio Practice Player"
        returnTo="/audio"
      />
    </div>
  );
}
